import React, { useCallback, useEffect, useState } from 'react';
import { Edit } from '@material-ui/icons';
import Box from '@material-ui/core/Box';
import TableContainer from '@material-ui/core/TableContainer';
import { useSnackbar } from 'notistack';
import Table from '@material-ui/core/Table';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import { Dialog } from '@material-ui/core';
import { Field, FieldProps, Form, Formik } from 'formik';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import LinearProgress from '@material-ui/core/LinearProgress';
import DialogActions from '@material-ui/core/DialogActions';
import makeStyles from '@material-ui/core/styles/makeStyles';
import moment from 'moment';
import TablePagination from 'components/atoms/TablePagination/TablePagination';
import Spin from 'components/atoms/Spin/Spin';
import { useDictionaryApi } from 'api/dictionary';
import FormikDatePicker from '../../components/atoms/FormikDatePicker/FormikDatePicker';
import { initWorkingDaysType, WorkingDaysType } from './types/WorkingDaysType';

const useStyles = makeStyles((theme) => ({
    root: {
        maxWidth: 380,
        width: '100%',
    },
    mt5: {
        marginTop: theme.spacing(3),
    },
}));

const WorkingDays = () => {
    const classes = useStyles();
    const { enqueueSnackbar } = useSnackbar();
    // Table and stuff
    const [loading, setLoading] = useState(false);
    const [items, setItems] = useState<WorkingDaysType[]>([]);
    const [item, setItem] = useState<Omit<WorkingDaysType, 'assign'>>(initWorkingDaysType());
    const [isDictionaryOpen, setDictionaryOpen] = useState(false);

    // Pagination
    const [page, setPage] = useState(0);
    const [perPage, setPerPage] = useState(50);
    const [total, setTotal] = useState(0);
    const { getPaged } = useDictionaryApi<WorkingDaysType>('/working-day/get-page');
    const { post, put } = useDictionaryApi<Omit<WorkingDaysType, 'assign'>>('/working-day');

    // Fetch
    const fetch = useCallback(async () => {
        setLoading(true);
        try {
            const x = await getPaged(page, perPage, 'requestDate,DESC', null);
            setItems(x.content);
            setTotal(x.total_elements);
        } catch {
            enqueueSnackbar('Ошибка при получении данных', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    }, [getPaged, page, perPage, enqueueSnackbar]);

    const handleAdd = () => {
        setItem(initWorkingDaysType());
        setDictionaryOpen(true);
    };

    const handleEdit = (item: WorkingDaysType) => {
        const request_date = moment(item.request_date, 'DD.MM.YYYY').toDate();
        const deadline_date = moment(item.deadline_date, 'DD.MM.YYYY').toDate();
        const formattedItem = {
            id: item.id,
            request_date,
            deadline_date,
        };
        setItem(formattedItem);
        setDictionaryOpen(true);
    };

    useEffect(() => {
        fetch();
    }, [fetch]);

    return (
        <Box position={'relative'}>
            <Button variant="contained" color="primary" onClick={handleAdd}>
                Добавить
            </Button>
            <Box position={'relative'}>
                {loading && <Spin />}
                <TableContainer>
                    <Table size={'small'} aria-label="simple table">
                        <TableHead>
                            <TableRow>
                                <TableCell>Идентификатор</TableCell>
                                <TableCell>Текущая дата</TableCell>
                                <TableCell>Дата окончания</TableCell>
                                <TableCell>Исполнитель</TableCell>
                                <TableCell>Действия</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {items.map((item, _index) => (
                                <TableRow key={item.id} hover={true}>
                                    <TableCell>{item.id}</TableCell>
                                    <TableCell>{item.request_date}</TableCell>
                                    <TableCell>{item.deadline_date}</TableCell>
                                    <TableCell>{item.assign}</TableCell>
                                    <TableCell>
                                        <IconButton onClick={() => handleEdit(item)}>
                                            <Edit />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
            <TablePagination
                total={total}
                perPage={perPage}
                page={page}
                onChangePage={setPage}
                onChangeRowsPerPage={setPerPage}
            />
            <Dialog
                open={isDictionaryOpen}
                fullWidth={true}
                onClose={() => setDictionaryOpen(false)}
            >
                <Formik
                    enableReinitialize={true}
                    initialValues={item}
                    onSubmit={(values, { setSubmitting }) => {
                        const request_date = moment(values.request_date).format('DD.MM.YYYY');
                        const deadline_date = moment(values.deadline_date).format('DD.MM.YYYY');
                        const formattedValues = {
                            ...values,
                            request_date,
                            deadline_date,
                        };
                        setSubmitting(true);
                        if (values.id) {
                            put(formattedValues)
                                .then(() => {
                                    setDictionaryOpen(false);
                                    fetch();
                                })
                                .catch(() =>
                                    enqueueSnackbar('Ошибка при сохранении данных', {
                                        variant: 'error',
                                    })
                                )
                                .finally(() => setSubmitting(false));
                        } else {
                            post(formattedValues)
                                .then(() => {
                                    setDictionaryOpen(false);
                                    fetch();
                                })
                                .catch(() =>
                                    enqueueSnackbar('Ошибка при сохранении данных', {
                                        variant: 'error',
                                    })
                                )
                                .finally(() => setSubmitting(false));
                        }
                    }}
                >
                    {({ isSubmitting }) => (
                        <Form className={classes.root}>
                            <DialogTitle>Редактирвание действии</DialogTitle>
                            <DialogContent>
                                <Box display="flex" flexDirection="column">
                                    <Field name="request_date">
                                        {({ ...props }: FieldProps) => (
                                            <FormikDatePicker
                                                className={classes.mt5}
                                                label="Текущая дата"
                                                inputVariant="standard"
                                                clearable
                                                disableToolbar={false}
                                                {...props}
                                            />
                                        )}
                                    </Field>
                                    <Field name="deadline_date">
                                        {({ ...props }: FieldProps) => (
                                            <FormikDatePicker
                                                className={classes.mt5}
                                                label="Дата окончания"
                                                inputVariant="standard"
                                                clearable
                                                disableToolbar={false}
                                                {...props}
                                            />
                                        )}
                                    </Field>
                                </Box>

                                {isSubmitting && <LinearProgress />}
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={() => setDictionaryOpen(false)}>Отмена</Button>
                                <Button type={'submit'}>Ок</Button>
                            </DialogActions>
                        </Form>
                    )}
                </Formik>
            </Dialog>
        </Box>
    );
};

export default WorkingDays;
